/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';

type SearchInputColor = 'white' | 'ice';

/**
 * A controlled search input component with an optional clear button.
 *
 * This component allows users to input text and provides a callback for handling changes.
 * It also supports a controlled value and updates its internal state when the controlled value changes.
 */
export const SearchInput = ({
  color = 'white',
  onChange,
  placeholder = 'Search...',
  value: controlledValue = '',
}: {
  color?: SearchInputColor;
  onChange: (value: string) => void;
  placeholder?: string;
  value?: string;
}) => {
  const [value, setValue] = useState(controlledValue);
  useEffect(() => {
    setValue(controlledValue);
  }, [controlledValue]);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    onChange(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleChange(newValue);
  };

  return (
    <fieldset css={containerStyles(color)} data-testid="nds-component-search-input">
      <BoulderIcon icon="search" css={searchIconStyles(!!value)} />
      <input
        type="search"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        css={inputStyles}
        data-testid="search-input"
      />
      {value && (
        <button
          type="button"
          onClick={() => handleChange('')}
          css={closeButtonStyles}
          data-testid="clear-search-button"
        >
          <BoulderIcon icon="delete" css={closeIconStyles} />
        </button>
      )}
    </fieldset>
  );
};

const containerStyles = (color: SearchInputColor) => css`
  align-items: center;
  background: ${color === 'ice' ? Colors.Ice : Colors.White};
  border: none;
  display: flex;
  height: ${Spacing.Unit2 * 6}px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const searchIconStyles = (hasSearched: boolean) => css`
  color: ${hasSearched ? Colors.BlueSpruce : Colors.CoolGray5};
  height: ${Spacing.Unit2 * 3}px;
  margin: ${Spacing.Unit1 * 3}px ${Spacing.Unit2 * 1.5}px;
  width: ${Spacing.Unit2 * 3}px;
`;

const closeButtonStyles = css`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  height: 100%;
  justify-content: center;
  padding: 0;

  &:hover {
    svg {
      color: ${Colors.BlueSpruce};
    }
  }
`;

const closeIconStyles = css`
  height: ${Spacing.Unit2 * 3}px;
  width: ${Spacing.Unit2 * 3}px;
  margin: ${Spacing.Unit1 * 3}px ${Spacing.Unit2 * 1.5}px;
  color: ${Colors.CoolGray5};
`;

const inputStyles = css`
  ${TypographyStyles.body1};

  background: transparent;
  border: none;
  color: ${Colors.CoolGray9};
  flex: 1;
  height: ${Spacing.Unit2 * 4}px;
  line-height: ${Spacing.Unit2 * 4}px;
  outline: none;
  padding: ${Spacing.Unit2}px 0;

  &::placeholder {
    ${TypographyStyles.body1};

    color: ${Colors.CoolGray6};
    font-style: italic;
  }

  /* clears the 'X' from Internet Explorer */
  &[type='search']::-ms-clear,
  &[type='search']::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  /* clears the 'X' from Chrome */
  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    display: none;
  }
`;
